// angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// cc-common imports
import { CCLocalStorageCategory, CCLocalStorageService, IconConstants, MessageBoxComponent, Utils } from 'cc-common';

// service imports
import { HelperService } from '../../../_services/shared-services/helper.service';
import { WorkflowSharedService } from '../../_services/workflow-shared.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { UserService } from '../../../_services/data-services/user.service';

// model imports
import { WorkflowDetailsResultDto } from '../../_models/workflow-details-result-dto';
import { WorkflowInfo } from '../../_models/workflow-info';
import { WorkFlowParametersDto } from '../../_models/workflow-parameters-dto';

// constant imports
import { Constants } from '../../../_constants/constants';
import { StorageKeyConstants } from '../../../_constants/storage-key-constants';

@Component({
    selector: 'app-workflow-detail-viewer',
    templateUrl: './workflow-detail-viewer.component.html',
    styleUrls: ['./workflow-detail-viewer.component.scss']
})
export class WorkflowDetailViewerComponent implements OnInit {
    @ViewChild('workflowDetailMessageBox', { static: false }) workflowDetailMessageBox: MessageBoxComponent;

    @Input() workflowId: number;
    @Input() communityId: number;

    @Output() splitterPositionChange = new EventEmitter<boolean>();
    @Output() hideSplitter = new EventEmitter<boolean>();
    @Output() pdfLoader = new EventEmitter<boolean>();
    @Output() pdfURL = new EventEmitter<string>();
    @Output() onClose = new EventEmitter<boolean>();
    @Output() onReclassifyWorkflow = new EventEmitter<WorkflowDetailsResultDto>();

    workflowResult: WorkflowDetailsResultDto;
    workflowInfo: WorkflowInfo;

    separateWindow = false;
    pdfViewerToLeft = false;
    settingsMenu: object[] = [];

    pdfWindow: any;
    pdfSource: string = null;
    pdfFileName: string;

    iconNotes: string = IconConstants.NOTES_AVAILABLE;
    iconEmptyNotes: string = IconConstants.NOTES_EMPTY;
    iconPDF: string = IconConstants.PDF_FILE;
    iconHistory: string = IconConstants.HISTORY;
    iconClose: string = IconConstants.CLOSE;
    iconCross: string = IconConstants.CROSS;
    iconCheck: string = IconConstants.CHECK;
    iconDelete: string = IconConstants.DELETE;

    loadingVisible = false;

    isInternalUser: boolean;

    constructor(
        private readonly workflowSharedService: WorkflowSharedService,
        private readonly errorService: ServerErrorService,
        private readonly helperService: HelperService,
        private readonly localStorageService: CCLocalStorageService,
        private readonly userService: UserService
    ) {}

    ngOnInit() {
        this.isInternalUser = this.userService.isInternal();
        this.getSplitterContentPosition();
        this.loadWorkflowDetail();
    }

    onForceRefresh(): void {
        this.loadWorkflowDetail();
    }

    onViewerClose(): void {
        this.onClose.emit(false);
    }

    private loadWorkflowDetail(): void {
        const baseParam: WorkFlowParametersDto = new WorkFlowParametersDto();
        baseParam.workflowId = this.workflowId;
        baseParam.communityId = this.communityId;
        baseParam.clearCache = true;

        this.loadingVisible = true;
        this.workflowSharedService.getWorkflowDetail(baseParam).subscribe(
            (result: WorkflowDetailsResultDto) => {
                this.workflowResult = result;
                if (Utils.isTrue(this.workflowResult.allowReclassify)) {
                    this.onReclassifyWorkflow.emit(this.workflowResult);
                } else {
                    this.getInvoice();
                    this.prepareWorkflowInfo();
                }
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private prepareWorkflowInfo(): void {
        this.workflowInfo = new WorkflowInfo();
        Object.keys(this.workflowResult).forEach((key) => (this.workflowInfo[key] = this.workflowResult[key]));
    }

    private getInvoice(): void {
        const baseParams: WorkFlowParametersDto = new WorkFlowParametersDto();
        baseParams.workflowId = this.workflowId;
        baseParams.communityId = this.communityId;

        if (!this.separateWindow) {
            this.pdfLoader.emit(true);
        }
        this.workflowSharedService.downloadInvoice(baseParams).subscribe(
            (result: Blob) => {
                if (!!result) {
                    const fileURL: string = this.helperService.getFileUrlFromBlob(result);

                    this.pdfURL.emit(fileURL);
                    if (!this.separateWindow) {
                        this.pdfLoader.emit(false);
                    }
                    if (this.separateWindow) {
                        this.showPDF();
                    }
                }
            },
            (error) => {
                if (error.status === 404) {
                    this.workflowDetailMessageBox.showErrorMessage(
                        'Error',
                        'Something went wrong, We are unable to find the invoice. Please try again later.'
                    );
                } else {
                    this.workflowDetailMessageBox.showErrorMessage('Error', error.error);
                }
                this.pdfSource = null;
                this.pdfURL.emit(null);
                if (!this.separateWindow) {
                    this.pdfLoader.emit(false);
                }
            }
        );
    }

    private getSplitterContentPosition(): void {
        this.separateWindow = false;
        this.pdfViewerToLeft = JSON.parse(
            this.localStorageService.getItem(StorageKeyConstants.wfInvoiceViewerPosition, CCLocalStorageCategory.WFE) ||
                String(Constants.splitterPDFViewerToLeft)
        );
        this.setSettingsMenu();
        this.splitterPositionChange.emit(this.pdfViewerToLeft);
    }

    private setSettingsMenu(): void {
        const settingsMenu = [
            {
                text: 'Show Invoice On Right Pane',
                visible: this.pdfViewerToLeft,
                onClick: () => {
                    this.pdfViewerToLeft = false;
                    this.splitterPositionChange.emit(this.pdfViewerToLeft);
                    this.setSettingsMenu();
                }
            },
            {
                text: 'Show Invoice On Left Pane',
                visible: !this.pdfViewerToLeft,
                onClick: () => {
                    this.pdfViewerToLeft = true;
                    this.splitterPositionChange.emit(this.pdfViewerToLeft);
                    this.setSettingsMenu();
                }
            },
            {
                text: 'Separate Window',
                visible: !this.separateWindow,
                onClick: () => {
                    this.separateWindow = true;
                    this.hideSplitter.emit(this.separateWindow);
                    this.showPDF();
                    this.changeSettingsMenu();
                }
            }
        ];

        this.settingsMenu = settingsMenu;
    }

    private showPDF(): void {
        if (this.pdfWindow && !this.pdfWindow.closed) {
            this.pdfWindow.close();
        }
        this.pdfWindow = window.open(
            this.pdfSource,
            '_blank',
            'location=yes,height=670,width=620,scrollbars=yes,status=yes'
        );
    }

    private changeSettingsMenu(): void {
        const settingsMenu = [
            {
                text: 'Show Side by Side Panel',
                onClick: () => {
                    this.separateWindow = false;
                    this.hideSplitter.emit(this.separateWindow);
                    this.setSettingsMenu();
                }
            }
        ];

        this.settingsMenu = settingsMenu;
    }
}
